import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

export default function SelfTestsPage() {
  const [tests, setTests] = useState([]);
  const [activeTest, setActiveTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    API.get("/tests").then(res => {
      setTests(res.data.tests);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const startTest = async (testId) => {
    try {
      const res = await API.get(`/tests/${testId}`);
      setActiveTest(res.data);
      setQuestions(res.data.questions);
      setAnswers({});
      setResult(null);
    } catch { toast.error("Kunde inte ladda testet"); }
  };

  const submitTest = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error("Svara på alla frågor först");
      return;
    }
    setSubmitting(true);
    try {
      const res = await API.post(`/tests/${activeTest.id}/submit`, { answers });
      setResult(res.data);
      const updated = await API.get("/tests");
      setTests(updated.data.tests);
    } catch { toast.error("Kunde inte skicka testet"); }
    setSubmitting(false);
  };

  const resetView = () => {
    setActiveTest(null);
    setQuestions([]);
    setAnswers({});
    setResult(null);
  };

  const SCALE_LABELS = ["Stämmer inte alls", "Stämmer sällan", "Ibland", "Stämmer ofta", "Stämmer helt"];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // RESULT VIEW
  if (result) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
          <div className="max-w-xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">{activeTest.subtitle}</p>
              <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3" data-testid="test-result-title">
                {result.result_title}
              </h1>
              <p className="text-sm text-[#111111]/40 mb-8">{result.score} av {result.max_score} poäng</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white border border-[#EBE3DF] rounded-2xl p-8 mb-10 text-left"
              data-testid="test-result-text"
            >
              <p className="text-[#111111]/70 leading-relaxed text-base">{result.result_text}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <Button onClick={resetView} variant="outline" className="rounded-full" data-testid="back-to-tests-btn">
                Alla tester
              </Button>
              <Button onClick={() => { resetView(); startTest(activeTest.id); }} className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full" data-testid="retake-test-btn">
                Gör om testet
              </Button>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ACTIVE TEST VIEW
  if (activeTest) {
    const allAnswered = Object.keys(answers).length === questions.length;
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
          <div className="max-w-xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <button onClick={resetView} className="flex items-center text-[#A0655A] mb-8 hover:underline text-sm" data-testid="back-from-test">
                <ChevronLeft size={16} className="mr-1" /> Tillbaka till tester
              </button>

              <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-2">{activeTest.subtitle}</p>
              <h1 className="heading-font text-2xl sm:text-3xl font-medium text-[#111111] mb-3" data-testid="active-test-title">{activeTest.title}</h1>
              <p className="text-[#111111]/50 text-sm mb-10 leading-relaxed">{activeTest.description}</p>
            </motion.div>

            <div className="space-y-6">
              {questions.map((q, idx) => (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * idx }}
                  className="bg-white border border-[#EBE3DF] rounded-2xl p-6"
                  data-testid={`question-${q.id}`}
                >
                  <p className="text-[#111111] text-sm font-medium mb-4 leading-relaxed">
                    <span className="text-[#A0655A] mr-2">{idx + 1}.</span>{q.text}
                  </p>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(val => (
                      <button
                        key={val}
                        onClick={() => setAnswers(prev => ({ ...prev, [q.id]: val }))}
                        className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all ${
                          answers[q.id] === val
                            ? 'bg-[#A0655A] text-white shadow-sm'
                            : 'bg-[#F9F3EF] text-[#111111]/50 hover:bg-[#EBE3DF]'
                        }`}
                        data-testid={`answer-${q.id}-${val}`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between mt-2">
                    <span className="text-[10px] text-[#111111]/30">{SCALE_LABELS[0]}</span>
                    <span className="text-[10px] text-[#111111]/30">{SCALE_LABELS[4]}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-10 text-center"
            >
              <Button
                onClick={submitTest}
                disabled={!allAnswered || submitting}
                className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-10 py-6 text-base disabled:opacity-40"
                data-testid="submit-test-btn"
              >
                {submitting ? 'Beräknar...' : 'Se mitt resultat'}
              </Button>
              {!allAnswered && (
                <p className="text-xs text-[#111111]/30 mt-3">Svara på alla {questions.length} frågor för att se ditt resultat</p>
              )}
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // TEST LIST VIEW
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-14"
          >
            <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">Utforska dig själv</p>
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#111111] mb-3" data-testid="tests-page-title">
              Självtester
            </h1>
            <p className="text-[#111111]/50 text-base max-w-md mx-auto leading-relaxed">
              Tester som hjälper dig att förstå var du befinner dig just nu. Du kan göra om dem när som helst.
            </p>
          </motion.div>

          <div className="space-y-4">
            {tests.map((test, idx) => (
              <motion.button
                key={test.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                onClick={() => startTest(test.id)}
                className="w-full bg-white border border-[#EBE3DF] rounded-2xl p-6 text-left hover:shadow-lg transition-all group"
                data-testid={`test-card-${test.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-wide text-[#A0655A] font-medium mb-1.5">{test.subtitle}</p>
                    <h2 className="heading-font text-xl font-medium text-[#111111] mb-1">{test.title}</h2>
                    <p className="text-sm text-[#111111]/40">{test.question_count} frågor</p>
                    {test.last_result && (
                      <p className="text-xs text-[#A0655A]/70 mt-2">
                        Senaste resultat: {test.last_result.title} ({test.last_result.score} p)
                      </p>
                    )}
                  </div>
                  <div className="w-12 h-12 rounded-full bg-[#FBF5F2] flex items-center justify-center group-hover:bg-[#A0655A] transition-colors">
                    <ChevronRight className="text-[#A0655A] group-hover:text-white transition-colors" size={20} />
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
